import { GridList, GridListItem } from 'react-aria-components';
import { Trans, useLingui } from '@lingui/react/macro';

import { Card, CardContent, CardItem, CardItems } from '@/components/Card/Card';
import { useNavigate } from '@/components/Link/Link';
import Box from '@/design_system/Box';
import Stack from '@/design_system/Stack';
import { Stepper } from '@/design_system/Stepper/Stepper';
import InlineText from '@/design_system/Typography/InlineText';
import IconArchive from '@/icons/Archive.svg';
import IconCloth from '@/icons/Cloth.svg';
import IconDone from '@/icons/Done.svg';
import { ShipmentWithRelations, useCarrierName } from '@/models/shipment';
import { useShipmentStepsToDisplay } from '@/routes/Shipments/Shipment/components/ShipmentStep/ShipmentStep';
import { ShipmentStepDueDate } from '@/routes/Shipments/Tabs/components/ShipmentStepDueDate';
import { createBEMClasses } from '@/utils/classname';

import { ShipmentDestination } from '../ShipmentDestination';
import { ShipmentOrigin } from '../ShipmentOrigin';

import './MobileShipmentsList.css';

const { block, element } = createBEMClasses('mobile-shipments-list');

export const MobileShipmentsList = ({
  shipments,
  showStepDueDate,
  pagination,
  isLoading,
}: {
  shipments: ShipmentWithRelations[];
  showStepDueDate?: boolean;
  pagination?: React.ReactNode;
  isLoading?: boolean;
}) => {
  const { t } = useLingui();
  const navigate = useNavigate();

  return (
    <>
      {!isLoading && (
        <GridList
          className={block()}
          aria-label={t({
            id: 'shipments.table.label',
            message: 'Shipments',
          })}
          onAction={(id) => navigate(`/shipments/${id}`)}
        >
          {shipments?.map((shipment) => (
            <ShipmentCard key={shipment.id} shipment={shipment} showStepDueDate={showStepDueDate} />
          ))}
        </GridList>
      )}
      {shipments?.length > 0 && !isLoading && pagination}
    </>
  );
};

const ShipmentCard = ({
  shipment,
  showStepDueDate,
}: {
  shipment: ShipmentWithRelations;
  showStepDueDate?: boolean;
}) => {
  const carrierName = useCarrierName(shipment);

  return (
    <GridListItem id={shipment.id} textValue={shipment.reference} className={element('card')}>
      <Card>
        <CardContent>
          <Stack flexWrap="nowrap" gap="0.5rem">
            <Stack>
              <InlineText size="200" ellipsis>
                {shipment.reference}
              </InlineText>
              {!!shipment.carrier && (
                <InlineText size="200" weight="medium">
                  {carrierName}
                </InlineText>
              )}
            </Stack>

            <Box>
              <Stack gap="0.75rem">
                <Stack>
                  <InlineText size="400">
                    <Trans id="shipments.table.from">FROM</Trans>
                  </InlineText>
                  <ShipmentOrigin shipment={shipment} />
                </Stack>
                <Stack>
                  <InlineText size="400">
                    <Trans id="shipments.table.to">TO</Trans>
                  </InlineText>
                  <ShipmentDestination shipment={shipment} />
                </Stack>
              </Stack>
            </Box>
          </Stack>
        </CardContent>

        <CardItems>
          <ArticlesCardItem shipment={shipment} />
          <StepCardItem shipment={shipment} />
          {showStepDueDate && <ShipmentStepDueDate shipment={shipment} variant="card" />}
        </CardItems>
      </Card>
    </GridListItem>
  );
};

const ArticlesCardItem = ({ shipment }: { shipment: ShipmentWithRelations }) => (
  <CardItem>
    <IconCloth style={{ fontSize: '1rem' }} />
    <span>{shipment.articles.length}</span>
  </CardItem>
);

const StepCardItem = ({ shipment }: { shipment: ShipmentWithRelations }) => {
  const steps = useShipmentStepsToDisplay(shipment);

  if (shipment.status === 'validated') {
    return (
      <CardItem className="text-success">
        <IconDone style={{ fontSize: '1rem' }} />
        <Trans id="shipment.status.validated">Validated</Trans>
      </CardItem>
    );
  }

  if (shipment.archivedAt) {
    return (
      <CardItem className="text-warning">
        <IconArchive style={{ fontSize: '1rem' }} />
        <Trans id="shipment.status.archived">Archived</Trans>
      </CardItem>
    );
  }

  return (
    <CardItem>
      <Stepper variant="round" size="x-small" steps={steps} />
    </CardItem>
  );
};
